import React from 'react';
import { Badge } from 'antd';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { KanbanBucket as KanbanBucketType } from '@/api/types/kanban.types';
import { KanbanItem } from './KanbanItem';

interface KanbanBucketProps {
  bucket: KanbanBucketType;
  isDraggingBucket?: boolean;
  activeItemId?: string | null;
  overId?: string | null;
}

/**
 * Kanban Bucket (Column) Component
 * Phase 4: Drag and drop support for buckets and items
 */
export const KanbanBucket: React.FC<KanbanBucketProps> = ({
  bucket,
  isDraggingBucket,
  activeItemId,
  overId,
}) => {
  const itemCount = bucket.items.length;

  // Check if we should show drop placeholder
  const shouldShowPlaceholder =
    activeItemId && overId && overId.startsWith('item-');
  const overItemId = overId?.replace('item-', '');

  // Make bucket sortable (for bucket reordering)
  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `bucket-${bucket.id}`,
    data: {
      type: 'bucket',
      bucket,
    },
  });

  // Make bucket droppable (for dropping items)
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `bucket-${bucket.id}`,
    data: {
      type: 'bucket',
      bucket,
    },
  });

  const style = {
    transform: isDragging ? 'none' : CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
    opacity: isDragging ? 0.3 : 1,
  };

  // Combine refs
  const setRefs = (node: HTMLDivElement | null) => {
    setSortableRef(node);
    setDroppableRef(node);
  };

  const itemIds = bucket.items.map((item) => `item-${item.id}`);

  return (
    <div
      ref={setRefs}
      style={style}
      className={`kanban-bucket ${isOver ? 'kanban-bucket-over' : ''} ${isDragging ? 'kanban-bucket-dragging' : ''}`}
    >
      <div className="kanban-bucket-header" {...attributes} {...listeners}>
        <h3 className="kanban-bucket-title">
          {bucket.title}
          <Badge
            count={itemCount}
            showZero
            style={{
              marginLeft: '8px',
              backgroundColor: 'transparent',
              color: '#00b4d8',
              border: '1px solid rgba(0, 180, 216, 0.3)',
              fontSize: '11px',
              fontWeight: 600,
            }}
          />
        </h3>
        <div
          className="kanban-bucket-add-btn"
          onClick={(e) => e.stopPropagation()}
        >
          +
        </div>
      </div>
      <div className="kanban-bucket-body">
        {itemCount === 0 ? (
          <>
            <button
              className="kanban-add-item-btn"
              onClick={(e) => e.stopPropagation()}
            >
              + Dodaj novi tiket
            </button>
            <div className="kanban-bucket-empty">No items</div>
          </>
        ) : (
          <>
            <button
              className="kanban-add-item-btn"
              onClick={(e) => e.stopPropagation()}
            >
              + Dodaj novi tiket
            </button>
            <SortableContext
              items={itemIds}
              strategy={verticalListSortingStrategy}
            >
              <div className="kanban-bucket-items">
                {bucket.items.map((item) => {
                  const isOverThisItem =
                    shouldShowPlaceholder && item.id === overItemId;
                  const isDraggingThisItem = activeItemId === item.id;

                  return (
                    <React.Fragment key={item.id}>
                      {isOverThisItem && !isDraggingThisItem && (
                        <div className="kanban-item-placeholder">Drop here</div>
                      )}
                      <KanbanItem item={item} />
                    </React.Fragment>
                  );
                })}
              </div>
            </SortableContext>
            <button
              className="kanban-add-item-btn"
              onClick={(e) => e.stopPropagation()}
            >
              + Dodaj novi tiket
            </button>
          </>
        )}
      </div>
    </div>
  );
};
